import React, { Component, ErrorInfo, ReactNode } from "react";
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Paper, Box 
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';

interface Props {
  children?: ReactNode;
  onClose?: () => void;
  /** * 'dialog': Shows a popup modal (Good for Wizards/Modals)
   * 'inline': Shows a flat paper card (Good for page sections/AISummary) 
   */
  displayVariant?: 'dialog' | 'inline';
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class UniversalErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to your analytics service here
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const { displayVariant = 'inline', onClose } = this.props;
      const errorMessage = this.state.error?.message || "Unknown error";

      // --- OPTION A: Dialog Mode (For ProfileWizard) ---
      if (displayVariant === 'dialog') {
        return (
          <Dialog
            open={true}
            onClose={onClose}
            PaperProps={{ sx: { borderRadius: 4, maxWidth: 360, textAlign: 'center', py: 2 } }}
          >
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, px: 4 }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                bgcolor: 'rgba(255, 167, 38, 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <WarningAmberIcon sx={{ fontSize: 30, color: 'warning.main' }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Something didn't load right
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please close and try again. If the problem continues, it will be resolved shortly.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button onClick={onClose} variant="contained" disableElevation sx={{ borderRadius: 6, px: 4 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      }

      // --- OPTION B: Inline Mode (For AISummary) ---
      return (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 3, m: 2, 
            textAlign: 'center', 
            //bgcolor: 'rgba(211, 47, 47, 0.05)', // Light red tint
            //borderColor: 'rgba(211, 47, 47, 0.3)'
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <PsychologyAltIcon color="primary" fontSize="large" />
            <Typography variant="h7" color="primary">
              Sorry, we are unable to show this info requested at this time
            </Typography>
            <Typography variant="body2" color="text.secondary">
               {errorMessage}
            </Typography>
            {onClose && (
              <Button variant="outlined"  onClick={onClose} sx={{ mt: 1 }}>
                Reset / Close
              </Button>
            )}
          </Box>
        </Paper>
      );
    }

    return this.props.children;
  }
}